import React, { useEffect, useMemo } from 'react'
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { Link } from 'react-router-dom'

import { Routes } from '../../../navigation/config/routes'
import type { PropertyLeasingSeasonConfigListItem } from '../../LeasingSeasonConfig/useLeasingSeasonConfigApi'

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginTop: theme.spacing(1),
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
    },
    note: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginTop: theme.spacing(0.75),
    },
    field: {
        width: '100%',
        marginTop: theme.spacing(0.5),
    },
    editorLink: {
        fontWeight: 600,
    },
    menuName: {
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    menuMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
}))

function configIsReady(config: PropertyLeasingSeasonConfigListItem): boolean {
    if (typeof config.is_ready === 'boolean') {
        return config.is_ready
    }
    return (
        config.application_workbook_id != null &&
        config.guest_card_pack_id != null &&
        config.fee_policy_id != null &&
        config.signable_doc_set_id != null &&
        config.property_data_pack_id != null &&
        config.pipeline_config_id != null
    )
}

function startFromMeta(config: PropertyLeasingSeasonConfigListItem): string {
    const bound = config.bound_season
    if (bound?.name) {
        return `Used by ${bound.name}`
    }
    return 'Draft'
}

function startFromRank(config: PropertyLeasingSeasonConfigListItem): number {
    const status = config.bound_season?.status
    if (!status) return 0
    if (status === 'active') return 1
    if (status === 'upcoming') return 2
    return 3
}

interface SeasonConfigSourcePanelProps {
    leasingSeasonConfigs: PropertyLeasingSeasonConfigListItem[]
    leasingSeasonConfigId?: number | null
    onLeasingSeasonConfigIdChange: (configId: number) => void
}

export const SeasonConfigSourcePanel: React.FC<SeasonConfigSourcePanelProps> = ({
    leasingSeasonConfigs,
    leasingSeasonConfigId = null,
    onLeasingSeasonConfigIdChange,
}) => {
    const classes = useStyles()
    const readyConfigs = useMemo(() => {
        const ready = leasingSeasonConfigs.filter(configIsReady)
        return [...ready].sort((a, b) => {
            const rank = startFromRank(a) - startFromRank(b)
            if (rank !== 0) return rank
            return b.id - a.id
        })
    }, [leasingSeasonConfigs])
    const hasIncomplete = leasingSeasonConfigs.length > readyConfigs.length

    useEffect(() => {
        if (leasingSeasonConfigId != null) return
        if (readyConfigs.length === 0) return
        onLeasingSeasonConfigIdChange(readyConfigs[0].id)
    }, [leasingSeasonConfigId, onLeasingSeasonConfigIdChange, readyConfigs])

    return (
        <Box className={classes.section}>
            <Typography variant="body2" className={classes.sectionTitle}>
                Start from
            </Typography>
            {readyConfigs.length > 0 ? (
                <>
                    <FormControl
                        variant="outlined"
                        size="small"
                        className={classes.field}
                        required
                    >
                        <InputLabel id="season-lsc-label">
                            Leasing Season Config
                        </InputLabel>
                        <Select
                            labelId="season-lsc-label"
                            label="Leasing Season Config"
                            value={leasingSeasonConfigId ?? ''}
                            onChange={(event) =>
                                onLeasingSeasonConfigIdChange(
                                    Number(event.target.value),
                                )
                            }
                            renderValue={(value) => {
                                const selected = readyConfigs.find(
                                    (row) => row.id === Number(value),
                                )
                                return selected?.name ?? ''
                            }}
                        >
                            {readyConfigs.map((config) => (
                                <MenuItem key={config.id} value={config.id}>
                                    <Box>
                                        <Typography
                                            className={classes.menuName}
                                        >
                                            {config.name}
                                        </Typography>
                                        <Typography
                                            className={classes.menuMeta}
                                        >
                                            {startFromMeta(config)}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {hasIncomplete ? (
                        <Typography className={classes.note}>
                            Incomplete configs are not listed.{' '}
                            <Link
                                className={classes.editorLink}
                                to={Routes.manage.leasingSeasonConfig.root}
                            >
                                Finish them in Leasing Season Config
                            </Link>
                            .
                        </Typography>
                    ) : (
                        <Typography className={classes.note}>
                            <Link
                                className={classes.editorLink}
                                to={Routes.manage.leasingSeasonConfig.root}
                            >
                                Manage configs in Leasing Season Config
                            </Link>
                        </Typography>
                    )}
                </>
            ) : (
                <Typography className={classes.note}>
                    {leasingSeasonConfigs.length > 0
                        ? 'No Leasing Season Config is ready to apply yet. Finish the required Setup and Compose slots in '
                        : 'Create a Leasing Season Config in '}
                    <Link
                        className={classes.editorLink}
                        to={Routes.manage.leasingSeasonConfig.root}
                    >
                        Leasing Season Config
                    </Link>{' '}
                    before starting a season.
                </Typography>
            )}
        </Box>
    )
}
